import React, { useMemo } from 'react';
import { cutting10units, numberWithCommas } from 'app/libs/number';
import TooltipPopper from 'app/components/common/TooltipPopper';
import Icon from 'app/components/common/Icon';
import dayjs from 'app/libs/dayjs';
import Button from 'app/components/common/Button';
import { ConsultantAdjustmentStatus } from 'app/containers/my/affiliate/PaymentContainer';
import YearMonthSelect from 'app/components/my/affiliate/YearMonthSelect';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';
import classNames from 'classnames/bind';
import styles from './PaymentCharge.module.scss';

const cx = classNames.bind(styles);

const PaymentCharge = ({
	now,
	firmName,
	selectedYearMonth,
	billSummaryData = {},
	consultantAdjustmentStatus = undefined,
	consultantAdjustmentAmount = undefined,
	dealerCountData,
	isQuarter = false,
	isCarryoverMonth = false,
	isPaid = false,
	isVirtualAccountWait = false,
	noData = false,
	disabledPayButton = false,
	diagnosis2PlusUseFlag = false,
	onChangeYearMonth,
	onClickPayment,
	onClickPaymentHistory,
	onClickHistoryMonth,
	onClickVirtualAccount,
}) => {
	const ua = useSsrUserAgent();
	const [year, month] = selectedYearMonth;
	const selectedYearMonthMoment = useMemo(
		() =>
			dayjs()
				.set('year', year)
				.set('month', month - 1),
		[year, month],
	);
	const minYearMonth = dayjs('2021-03-01');
	const maxYearMonth = useMemo(() => {
		const baseDate = dayjs(now);
		// 테스트 환경에서만 다음달까지 조회하기 위해
		return process.env.REACT_APP_DEPLOY_ENV !== 'prod' ? baseDate.add(1, 'month') : baseDate;
	}, [now]);
	const currentQuarter = Math.ceil(Number(month) / 3);
	const prevQuarter = currentQuarter - 1 || 4;
	const hasConsultantAdjustment =
		consultantAdjustmentStatus === ConsultantAdjustmentStatus.PROGRESS ||
		consultantAdjustmentStatus === ConsultantAdjustmentStatus.APPROVE_COMPLETE;

	const excessAmount = (() => {
		if (isQuarter && isCarryoverMonth) return billSummaryData.monthCarryingForwardAmount;
		return Math.max(billSummaryData.extraAmount, 0);
	})();

	const beforeAmount = billSummaryData.carAdjustmentAmount ? excessAmount + billSummaryData.carAdjustmentAmount : null;

	// 반응형 스타일
	const customStyle = {
		btnWidth: ua().isPc ? { width: '100px' } : { width: '100%' },
		btnHeight: ua().isPc ? '40' : '50',
		classPlus: ua().isPc ? 'uiico_plus2' : 'uiico_plus',
		classTotal: ua().isPc ? 'uiico_total2' : 'uiico_total',
		colWidth: ua().isPc ? '198px' : '140px',
		boxPadding: ua().isPc && hasConsultantAdjustment ? '290px' : '',
		boxWidth: ua().isPc && hasConsultantAdjustment ? '290px' : '',
	};

	return (
		<>
			<div className={cx('wrap_notice')}>
				<h3 className={cx('blind')}>청구금액 내역</h3>
				<div className={cx('box_select')}>
					<YearMonthSelect
						year={year}
						month={month}
						minYearMonth={minYearMonth}
						maxYearMonth={maxYearMonth}
						onChangeYearMonth={onChangeYearMonth}
					/>
				</div>
				<div className={cx('box_notice_payment')}>
					<p className={cx('txt_detail_money')}>
						<strong>{firmName}</strong> 청구금액 내역입니다.
					</p>
					<p className={cx('date_payment')}>결제기간 : {selectedYearMonthMoment.format('YYYY.MM.01 ~ YYYY.MM.05')}</p>
				</div>
			</div>
			<div className={cx('wrap_history')}>
				{noData === true ? (
					<p className={cx('no_data')}>제휴상품 이용내역이 없습니다.</p>
				) : (
					<div
						className={cx('box_detail_payment')}
						style={{ paddingRight: customStyle.boxPadding }}
					>
						<div className={cx('item_payment')}>
							<div className={cx('list_payment')}>
								<strong className={cx('tit_payment')}>{month}월 제휴상품 선납금액</strong>
								<strong className={cx('txt_price')}>{`${numberWithCommas(billSummaryData.contractAmount)}원`}</strong>
								<p className={cx('txt_number')}>계약대수 {numberWithCommas(billSummaryData.contractCnt)}대</p>
								<div className={cx('icon_plus')}>
									<Icon
										className={cx(customStyle.classPlus)}
										text="plus"
									/>
								</div>
							</div>
							<div className={cx('list_payment', billSummaryData.extraAmount === 0 && !isQuarter && 'no_amount')}>
								<strong className={cx('tit_payment')}>
									{isQuarter && !isCarryoverMonth ? `${prevQuarter}분기 초과상품 금액` : `${month - 1 || 12}월 초과상품 금액`}
									{isQuarter && isCarryoverMonth && <em className={cx('mark_quarter')}>분기</em>}
								</strong>
								<strong className={cx('txt_price', { not_affiliate: isCarryoverMonth })}>
									{beforeAmount ? (
										<>
											<span className={cx('txt_price_adjust')}>{numberWithCommas(beforeAmount)}원</span>
											{numberWithCommas(excessAmount)}원
											<TooltipPopper
												icon="uiico_question2"
												iconText="초과상품 금액 자세히 보기"
												btnStyle={{ margin: '-8px 0 0 5px' }}
												tooltip={
													<>
														차량별 금액조정 신청을 통해
														<br />
														금액이 조정 되었습니다.
													</>
												}
											/>
										</>
									) : (
										`${numberWithCommas(excessAmount)}원`
									)}
									{isQuarter && !disabledPayButton && (
										<button
											type="button"
											className={cx('btn_history_month')}
											onClick={onClickHistoryMonth}
										>
											월별내역
										</button>
									)}
								</strong>
							</div>
							{hasConsultantAdjustment && (
								<div className={cx('list_payment', 'consultant_adjust')}>
									<strong className={cx('tit_payment')}>컨설턴트 조정 금액</strong>
									{consultantAdjustmentStatus === ConsultantAdjustmentStatus.APPROVE_COMPLETE ? (
										<strong className={cx('txt_price')}>{numberWithCommas(consultantAdjustmentAmount)}원</strong>
									) : (
										<strong className={cx('txt_price')}>조정중</strong>
									)}
									<div className={cx('icon_plus')}>
										<Icon
											className={cx(customStyle.classPlus)}
											text="plus"
										/>
									</div>
								</div>
							)}
						</div>
						<div
							className={cx('total_payment')}
							style={{ width: customStyle.boxWidth }}
						>
							<div className={cx('icon_total')}>
								<Icon
									className={cx(customStyle.classTotal)}
									text="total"
								/>
							</div>
							<strong className={cx('tit_payment')}>총 결제금액</strong>
							<strong className={cx('txt_price')}>{numberWithCommas(cutting10units(billSummaryData.totalAmount))}원</strong>
							<ul className={cx('item_person')}>
								<li>확인 {dealerCountData.confirmedCount}명</li>
								<li>조정중 {dealerCountData.adjustmentInProgressCount}명</li>
								<li>미확인 {dealerCountData.unConfirmedCount}명</li>
							</ul>
							{isPaid && (
								<button
									type="button"
									className={cx('link_history_pop')}
									onClick={onClickPaymentHistory}
								>
									결제완료
									{billSummaryData.totalAmount > 0 && (
										<>
											(<em className={cx('txt_point')}>결제내역조회</em>)
										</>
									)}
								</button>
							)}
							{isVirtualAccountWait && (
								<button
									type="button"
									className={cx('link_history_pop')}
									onClick={onClickVirtualAccount}
								>
									<em className={cx('txt_point')}>무통장입금 대기중</em>
								</button>
							)}
							{!isPaid && !isVirtualAccountWait && (
								<Button
									title="결제하기"
									disabled={disabledPayButton}
									size={customStyle.btnHeight}
									style={{ margin: '0 auto', ...customStyle.btnWidth }}
									onClick={onClickPayment}
								/>
							)}
							{diagnosis2PlusUseFlag && (
								<p className={cx('txt_notice')}>
									시스템 표기 금액과 계약 금액 간 차이가 발생할 경우,
									<br />
									계약서 상의 상품 금액을 최종 정산 기준으로 적용합니다.
								</p>
							)}
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default PaymentCharge;
